import React from 'react';
import { cn } from '@vehix/portal/lib/utils';
import type { PipelineDetail } from '../../services/pipelineOrchestrator';

/**
 * Date-range scope for the runs list.
 *
 * The orchestrator has no date parameters, so the range is applied on the
 * client over a single fetched window. Capped at 3 months, which covers 202 of
 * the 261 runs that exist all-time.
 */

export type RangeKey = '24h' | '7d' | '30d' | '3mo';

interface RangeOption {
  key: RangeKey;
  label: string;
  /** Lookback in days. */
  days: number;
}

export const RANGE_OPTIONS: RangeOption[] = [
  { key: '24h', label: '24 hours', days: 1 },
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '3mo', label: '3 months', days: 90 }
];

/** Tight enough to stay a single page at current volume (~3 runs/day). */
export const DEFAULT_RANGE: RangeKey = '7d';

export function isValidRange(value: string | null): value is RangeKey {
  return RANGE_OPTIONS.some(o => o.key === value);
}

/** The next widest range, for "nothing here — try a bigger window" prompts. */
export function widerRange(key: RangeKey): RangeOption | undefined {
  const i = RANGE_OPTIONS.findIndex(o => o.key === key);
  return i >= 0 ? RANGE_OPTIONS[i + 1] : undefined;
}

function startedAt(run: PipelineDetail): number {
  return new Date(run.started_at ?? run.requested_at).getTime();
}

/** Filters to the range and orders newest first. */
export function applyRange(runs: PipelineDetail[], key: RangeKey): PipelineDetail[] {
  const days = RANGE_OPTIONS.find(o => o.key === key)?.days;
  const cutoff = days === undefined ? -Infinity : Date.now() - days * 24 * 60 * 60 * 1000;
  return runs.filter(run => startedAt(run) >= cutoff).sort((a, b) => startedAt(b) - startedAt(a));
}

interface PipelineRangeFilterProps {
  value: RangeKey;
  onChange: (key: RangeKey) => void;
}

export const PipelineRangeFilter: React.FC<PipelineRangeFilterProps> = ({ value, onChange }) => (
  <div className='flex items-center gap-1.5'>
    <span className='text-xs text-muted-foreground'>Last:</span>
    <div className='flex items-center rounded-md border p-0.5'>
      {RANGE_OPTIONS.map(opt => (
        <button
          key={opt.key}
          type='button'
          onClick={() => onChange(opt.key)}
          aria-pressed={value === opt.key}
          className={cn(
            'rounded px-2 py-1 text-xs transition-colors',
            value === opt.key
              ? 'bg-primary/10 font-medium text-foreground'
              : 'text-muted-foreground hover:bg-muted/50'
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);
